import { EMPTY_STRING } from '@rotomeca/utils';

/**
 * @Attr : Synchronise une propriété (auto-accessor) avec un attribut HTML.
 * Gère dynamiquement les types string, boolean et number.
 */
export function Attr(attributeName?: string) {
	return function <This extends HTMLElement, Value>(
		_target: ClassAccessorDecoratorTarget<This, Value>,
		context: ClassAccessorDecoratorContext<This, Value>,
	): ClassAccessorDecoratorResult<This, Value> {
		const attrName = attributeName || String(context.name);

		return {
			get(this: This): Value {
				const val = this.getAttribute(attrName);

				// Logique de conversion selon la valeur de l'attribut
				// On utilise "as unknown as Value" pour satisfaire le compilateur
				if (val === EMPTY_STRING || val === 'true')
					return true as unknown as Value;
				if (val === null) return false as unknown as Value;

				// Si c'est un nombre (on vérifie si la conversion est possible)
				const num = Number(val);
				if (val !== EMPTY_STRING && !isNaN(num)) return num as unknown as Value;

				return val as unknown as Value;
			},
			set(this: This, value: Value) {
				if (value === null || value === undefined || value === false) {
					this.removeAttribute(attrName);
				} else {
					// Si c'est un booléen true, on met un attribut vide (pattern HTML standard)
					const strVal = value === true ? EMPTY_STRING : String(value);
					this.setAttribute(attrName, strVal);
				}
			},
		};
	};
}
type DataAccessortCallback = <This extends HTMLElement, Value>(
	_target: ClassAccessorDecoratorTarget<This, Value>,
	context: ClassAccessorDecoratorContext<This, Value>,
) => ClassAccessorDecoratorResult<This, Value>;

export function Data(
	name: string,
	options: { setter?: boolean },
): DataAccessortCallback;
export function Data(options: { setter?: boolean }): DataAccessortCallback;
export function Data(name: string): DataAccessortCallback;
export function Data(): DataAccessortCallback;
export function Data(
	nameOrOptions?: string | { setter?: boolean },
	maybeOptions?: { setter?: boolean },
): DataAccessortCallback {
	// 1. Déduction intelligente des arguments
	let attributeName: string | undefined;
	let setter = true;

	if (typeof nameOrOptions === 'string') {
		attributeName = nameOrOptions;
		setter = maybeOptions?.setter ?? true;
	} else if (typeof nameOrOptions === 'object') {
		setter = nameOrOptions.setter ?? true;
	}

	return function <This extends HTMLElement, Value>(
		_target: ClassAccessorDecoratorTarget<This, Value>,
		context: ClassAccessorDecoratorContext<This, Value>,
	): ClassAccessorDecoratorResult<This, Value> {
		// 2. Nettoyage du nom (ex: #_icon devient icon)
		// On enlève le # et le _ au début pour le nom de l'attribut data
		const autoName = String(context.name).replace(/^[#_]+/, '');
		const finalAttrName = attributeName || autoName;

		return {
			get(this: This): Value {
				const val =
					typeof (this as any).data === 'function'
						? (this as any).data(finalAttrName)
						: this.getAttribute(`data-${finalAttrName}`);

				if (val === EMPTY_STRING || val === 'true')
					return true as unknown as Value;
				if (val === null || val === undefined) return _target.get.call(this);

				const num = Number(val);
				if (val !== EMPTY_STRING && !isNaN(num)) return num as unknown as Value;

				return val as unknown as Value;
			},
			set(this: This, value: Value) {
				if (!setter) return;

				const isFalsy =
					value === null || value === undefined || value === false;
				const strVal = value === true ? EMPTY_STRING : String(value);

				if (typeof (this as any).data === 'function') {
					(this as any).data(finalAttrName, isFalsy ? null : strVal);
				} else {
					const domAttrName = `data-${finalAttrName}`;
					if (isFalsy) this.removeAttribute(domAttrName);
					else this.setAttribute(domAttrName, strVal);
				}
			},
		};
	};
}

/**
 * Option pour Data pour indiquer qu'un accessor ne devrait pas avoir de setter.
 *
 * @example
 * ```typescript
 * ///
 * @Data(NO_SETTER)
 * accessor #_label!: string;
 * ```
 */
export const NO_SETTER = { setter: false };
